<template>
  <el-select
    ref="select"
    v-model="model"
    :placeholder="placeholder"
    :size="size"
    :name="name"
    :filterable="filterable"
    :clearable="clearable"
    :automatic-dropdown="automaticDropdown"
    :autocomplete="autocomplete"
    :popper-class="popperClass"
    :popper-append-to-body="popperAppendToBody"
    :default-first-option="defaultFirstOption"
    :remote="remote"
    :remote-method="searchOptions"
    :loading="remoteLoading"
    :loading-text="loadingText"
    :no-match-text="noMatchText"
    :no-data-text="noDataText"
    :filter-method="filterOptions"
    :disabled="isDisabled"
    :multiple="multiple"
    :collapse-tags="collapseTags"
    :multiple-limit="multipleLimit"
    :value-key="valueKey"
    :allow-create="allowCreate"
    :reserve-keyword="reserveKeyword"
    :class="customClass"
    @click.native="onClick($event)"
    @keydown.enter.capture.native="onEnterKey($event)"
    @blur="onBlur"
    @focus="onFocus"
    @change="onChange"
    @visible-change="onVisibleChange"
    v-loading="loading">
    <component
      :is="optionComponent"
      v-for="item in filteredOptions"
      :key="item[itemKey]"
      :item="item">
    </component>
  </el-select>
</template>

<script>
/* eslint-disable semi */
import { Component, Prop, Watch } from 'vue-property-decorator';
import SasComponent from './SasComponent';
import * as sas from '../decorators';

// 選択オプションの描画コンポーネント
import SasSelectOption from './SasSelectOption';

/**
 * 選択要素の基底コンポーネント
 */
@Component({ components: { SasSelectOption } })
export default class SasSelect extends SasComponent {
  /*
   下記はコンポーネント側で管理します
   filter-method
   remote
   remote-method
   loading
   loading-text
   no-match-text
   no-data-text
   autocomplete
   default-first-option
   popper-append-to-body
   automatic-dropdown
   popper-class
   clearable
   filterable
   multiple
   collapse-tags
   multiple-limit
   value-key
   allow-create
   reserve-keyword
   必要に応じて、継承クラス側のコンポーネントで適宜指定してください。
   */

  /**
   * 親がv-modelでバインドしたデータの格納先プロパティ
   * v-model対応
   */
  @Prop({ required: true })
  value;

  @Prop({ required: false, type: String })
  placeholder;

  @Prop({ required: false, type: String })
  size;

  @Prop({ required: false, type: String })
  name;

  @Prop({ required: false, type: Boolean })
  disabled;

  get autofocus () {
    return true;
  }

  /**
   * フィルター入力可否
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get filterable () {
    return false;
  }

  /**
   * 選択値のクリア可否
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get clearable () {
    return true;
  }

  /**
   * ドロップダウンの自動展開
   *
   * フィルター機能を有効にしている場合は、この設定に関わらず、フォーカス
   * インしたタイミングで自動展開します。
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get automaticDropdown () {
    return false;
  }

  /**
   * 選択オプション要素のクラス指定
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get popperClass () {
    return null;
  }

  /**
   * 選択オプション要素の表示位置設定フラグ
   * falseを返却した場合、選択オプション要素の表示位置は下部固定となります。
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get popperAppendToBody () {
    return true;
  }

  /**
   * Select要素のautocomplete属性値
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get autocomplete () {
    return 'off';
  }

  /**
   * フィルター機能を有効にしている場合
   *
   * trueを返却した場合、フィルター条件を入力したエンターキーを入力した際に、
   * フィルター条件に合致する最初の選択オプションを選択します。
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get defaultFirstOption () {
    return false;
  }

  /**
   * 動的ローディング有効無効
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get remote () {
    return false;
  }

  /**
   * フィルター条件で合致する選択オプションが存在しない場合のメッセージ
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   * nullを返却した場合、ELのデフォルトの文言が適用されます。
   */
  get noMatchText () {
    return null;
  }

  /**
   * 動的ローディング中のメッセージ
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   * nullを返却した場合、ELのデフォルトの文言が適用されます。
   */
  get loadingText () {
    return null;
  }

  /**
   * 選択オプションが空の場合のメッセージ
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   * nullを返却した場合、ELのデフォルトの文言が適用されます。
   */
  get noDataText () {
    return null;
  }

  /**
   * 親から渡された値を変換した内部データ（コード階層配列）を参照する
   *
   * v-model対応
   */
  get model () {
    return this.value;
  }

  /**
   * 内部データを親に通知する
   */
  set model (value) {
    this.$emit('input', value);
  }

  /**
   * 有効無効状態を参照する
   *
   * NOTE
   * 親コンポーネントからPropで指定された状態と、自身が管理する状態
   * の双方を参照して有効無効状態を算出しています。
   */
  get isDisabled () {
    return this.disabled || this.innerDisabled;
  }

  /**
   * 選択項目要素のコンポーネント
   *
   * NOTE
   * 継承クラスでオプションの表示をカスタマイズする場合は、このメソッドをオーバー
   * ライドして、任意のコンポーネントを返却する。
   */
  get optionComponent () {
    return SasSelectOption;
  }

  /**
   * 選択項目要素のキー項目
   *
   * NOTE
   * キー項目として他の項目の値を参照する必要がある場合は、このメソッドをオーバー
   * ライドして、任意の項目名を返却する。
   */
  get itemKey () {
    return 'value';
  }

  /**
   * 選択項目要素のlabel値の項目名
   *
   * NOTE
   * ラベル項目として他の項目の値を参照する必要がある場合は、このメソッドをオーバー
   * ライドして、任意の項目名を返却する。
   */
  get labelKey () {
    return 'label';
  }

  /**
   * 複数選択の有効無効
   *
   * NOTE
   * 有効にする場合は、このメソッドをオーバーライドしてtrueを返却する。
   */
  get multiple () {
    return false;
  }

  /**
   * 複数選択時の選択値をタグとして表示する機能の有効無効
   *
   * NOTE
   * 有効にする場合は、このメソッドをオーバーライドしてtrueを返却する。
   */
  get collapseTags () {
    return false;
  }

  /**
   * 複数選択時の最大選択数
   *
   * NOTE
   * 最大数を設定する場合は、このメソッドをオーバーライドして任意の最大数を返却する。
   */
  get multipleLimit () {
    return 0;
  }

  /**
   * 値がオブジェクトの場合のキープロパティ名
   *
   * NOTE
   * 変更する場合は、このメソッドをオーバーライドして任意のプロパティ名を返却する。
   */
  get valueKey () {
    return 'value';
  }

  /**
   * 選択値を生成できる機能の有効無効
   *
   * NOTE
   * 有効にする場合は、このメソッドをオーバーライドしてtrueを返却する。
   * 併せてfilterableをtrueに設定する必要がある。
   */
  get allowCreate () {
    return false;
  }

  /**
   * 選択値を生成する際に現在のキーワードを予約する機能の有効無効
   *
   * NOTE
   * 有効にする場合は、このメソッドをオーバーライドしてtrueを返却する。
   * 併せてfilterableとmultipleをtrueに設定する必要がある。
   */
  get reserveKeyword () {
    return false;
  }

  /**
   * クラス指定
   */
  get customClass () {
    return null;
  }

  // NOTE
  // loading: コンポーネント自身のローディングフラグ
  // remoteLoading: 動的ローディング用のローディングフラグ
  @sas.BindLoading({ activeOn: ['fetch*'] })
  @sas.BindLoading({ label: 'remoteLoading', activeOn: ['searchOptions'] })
  @sas.BindCache({ transparent: true })
  @sas.BindApi
  @sas.BindDefault
  data () {
    return {
      filteredOptions: [],
      selectOptions: [],
      innerDisabled: false,
      loading: false,
      remoteLoading: false
    };
  }

  // 初期化
  initialize () {
    this.fetchOptions();
  }

  focus () {
    if (this.$refs.select) {
      this.$refs.select.focus();
    }
  }

  // 親コンポーネントへの透過イベント
  onBlur (...args) {
    this.$emit('blur', ...args);
  }

  // 親コンポーネントへの透過イベント
  onFocus (...args) {
    this.$emit('focus', ...args);
  }

  // 親コンポーネントへの透過イベント
  onChange (...args) {
    this.$emit('change', ...args);
  }

  // 親コンポーネントへの透過イベント
  onVisibleChange (...args) {
    // フィルターでの絞り込み状態が継続することを回避するため
    // 選択オプションが閉じるタイミングで内容をフルの状態に戻す
    this.filteredOptions = this.selectOptions;

    this.$emit('visible-change', ...args);
  }

  /**
   * 動的ローディングが有効な場合
   * valueが変更された時点で選択オプションを更新する
   */
  @Watch('value')
  onValueChanged () {
    this.remote && this.fetchOptions();
  }

  // エンターキーイベント
  onEnterKey (event) {
    // 継承クラス側で実装してください
  }

  // クリックイベント
  onClick (event) {
    // 継承クラス側で実装してください
  }

  /**
   * 選択オプションを取得する
   *
   * NOTE
   * 動的ローディングが有効な場合、自身のvalueに対応する選択オプションを取得する
   * 処理として実装する。
   */
  @sas.Asynchronous
  fetchOptions () {
    return this.fetchOptionsImpl()
      .then(options => {
        this.selectOptions = this.filteredOptions = options;
      })
      .catch(reason => {
        if (!reason.isCanceled) {
          this.innerDisabled = true;
          this.selectOptions = this.filteredOptions = this.$_sas.$default.selectOptions;
        }
        this.ignore(reason);
      });
  }

  /**
   * 選択オプションをサーバから取得する
   *
   * NOTE
   * この処理は継承クラス側で個別に実装する。
   * 成功時にはoptionsを返却するPromiseを返却するメソッドとして実装する。
   */
  fetchOptionsImpl () {
    throw new Error('SasSelect.fetchOptionsImpl must be overridden.');
  }

  /**
   * 動的ローディング処理
   *
   * el-selectのremote-methodとして関連付けしています。
   * remoteがtrueの場合のみ実行されます。
   */
  searchOptions (query) {
    return this.searchOptionsImpl(query)
      .then(options => {
        this.selectOptions = this.filteredOptions = options;
      })
      .catch(reason => {
        if (!reason.isCanceled) {
          this.innerDisabled = true;
          this.selectOptions = this.filteredOptions = this.$_sas.$default.selectOptions;
        }
        this.ignore(reason);
      });
  }

  /**
   * 検索条件を指定して選択オプションをサーバから取得する
   *
   * NOTE
   * この処理は継承クラス側で個別に実装する。
   * 成功時にはoptionsを返却するPromiseを返却するメソッドとして実装する。
   */
  searchOptionsImpl (query) {
    throw new Error('SasSelect.searchOptionsImpl must be overridden.');
  }

  /**
   * フィルター処理
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   * デフォルトはラベル文字列での曖昧検索によるフィルター処理。
   */
  filterOptions (query) {
    this.filteredOptions = this.selectOptions.filter(option => {
      return option[this.labelKey].includes(query);
    });
  }

  /**
   * 値が空か判定する
   */
  isEmpty (value = null) {
    return value === null || value === '';
  }

  /**
   * 指定した値がオプションの値に含まれるか判定する
   */
  isContainsValue (val) {
    if (val === null || val === undefined) {
      return false;
    }
    if (!this.filteredOptions) {
      return false;
    }
    const key = this.itemKey;
    return this.filteredOptions.some(item => {
      return val === item[key];
    });
  }
}
</script>



// WEBPACK FOOTER //
// src/core/ui/SasSelect.vue